/**
 * HERO THEME OVERRIDES (ADR-0099 / ADR-0102 / ADR-0117) — a hero recolours the kid shell from its OWN tokens.
 * This is NOT a full base theme (cozy-pastel / command-center / warm-neutral-teal): a hero overrides a
 * CONTRAST-PAIRED subset of the kid theme's vars, layered on top of the base kid theme while a hero is chosen
 * and cleared on logout / no-hero. Every overridable var carries a base-theme fallback in the kid stylesheet,
 * so with no hero the shell is byte-identical to the un-themed cozy-pastel look.
 *
 * ADR-0117 widens the override set from "brand + sky" to the WHOLE kid world — a hero now owns a brand RAMP
 * (its primary in soft / mid / deep / tint + a glow), the SKY group, and the page CANVAS wash. So choosing
 * royal vs dash retints buttons, meters, rings, badges, the page background AND the hero stage, not just a
 * couple of accents. What stays OUT of a hero's reach (and so guarantees legibility): body text, the light
 * canvas base + paper, hairline borders, and the success/warning/danger/coin/star roles. A hero cannot make
 * the kid UI unreadable — it can only retint the brand-coloured surfaces, each paired with its own foreground.
 *
 * A `defineHeroThemeContract` identity helper forces every hero to supply EVERY overridable var (no drift,
 * mirroring Panda's `defineThemeContract`); adding a var here is a compile error in every hero until authored.
 *
 * CONTRAST RULES (enforced by {@link validateHeroThemeContrast} + the web registry test) — the numeric floor a
 * palette must hold so the retinted shell stays legible:
 *   · `primary-fg` on `primary` ≥ 4.5 (button/badge text on the brand mid — WCAG AA body text)
 *   · `primary-fg` on `soft` ≥ 3.0 (the light TOP of the button/meter gradient; bold text over a blend)
 *   · `primary` vs white ≥ 3.0 (non-text uses: selection rings, meter fill, focus ring vs a card/page)
 *   · `deep` on the light page (#faf7f5) and on `tint` ≥ 4.5 (deep is the hero's on-light text shade)
 *   · `secondary-fg` on `secondary` ≥ 4.5 · `accent-fg` on `accent` ≥ 4.5 (chip/pill pairs)
 *   · `sky-fg` on `sky-top` AND `sky-mid` ≥ 4.5 (on-sky copy sits over the top two stops; `sky-low` is the
 *     HORIZON band where the figure stands — keep text off it, it is deliberately light and unchecked)
 */

/**
 * The CSS custom properties a hero may override (ADR-0117). Grouped by role; each colour that sits under text
 * is PAIRED with its foreground (or used only as a soft fill / non-text glow) so the hero author owns the
 * contrast of the pair, not the core. Deliberately EXCLUDES body text, the canvas base/paper, borders, and the
 * status/currency roles — those stay the kid theme's values so legibility never depends on a hero's palette.
 */
export const HERO_THEME_VARS = [
  // BRAND RAMP — the hero's primary identity. `primary` is the mid; `soft`/`deep` are the gradient + on-light
  // text shades; `tint` is the softest fill; `glow-rgb` is a SPACE-SEPARATED rgb triplet (e.g. `199 92 147`)
  // composed with an alpha in CSS (`rgb(var(--color-primary-glow-rgb) / 0.34)`) for the brand's coloured
  // shadows. These drive buttons, meters, focus rings, badges, chips and selection across the kid pages.
  '--color-primary',
  '--color-primary-fg',
  '--color-primary-soft',
  '--color-primary-deep',
  '--color-primary-tint',
  '--color-primary-glow-rgb',
  // SECONDARY + ACCENT — supporting brand fill + the playful highlight, each contrast-paired.
  '--color-secondary',
  '--color-secondary-fg',
  '--color-accent',
  '--color-accent-fg',
  // CANVAS WASH — the two soft radial blooms behind the kid page content (ambient world tint). They fade to
  // transparent and never sit under text, so a hero tints the atmosphere without touching legibility.
  '--canvas-a',
  '--canvas-b',
  // SKY — the kid hero scene's dusk gradient + a contrast-paired foreground for white-on-sky copy.
  '--sky-top',
  '--sky-mid',
  '--sky-low',
  '--sky-glow',
  '--sky-fg',
] as const;

export type HeroThemeVar = (typeof HERO_THEME_VARS)[number];

/** A hero's colour overrides — EVERY overridable var, authored as a CSS colour string. */
export type HeroThemeTokens = Readonly<Record<HeroThemeVar, string>>;

/**
 * Identity helper that TYPE-CHECKS a hero's theme against the contract: a hero cannot omit an overridable
 * var (every key is required) and cannot add an unknown one. Authoring a hero theme through this guarantees
 * the kid shell can apply it whole.
 */
export function defineHeroThemeContract(tokens: HeroThemeTokens): HeroThemeTokens {
  return tokens;
}

/** Apply a hero's overrides to an element (the kid shell root); they cascade to descendants as inline vars. */
export function applyHeroTheme(el: HTMLElement, tokens: HeroThemeTokens): void {
  for (const name of HERO_THEME_VARS) {
    el.style.setProperty(name, tokens[name]);
  }
}

/** Remove the hero overrides, restoring the base theme's values (the vars fall back to the stylesheet). */
export function clearHeroTheme(el: HTMLElement): void {
  for (const name of HERO_THEME_VARS) {
    el.style.removeProperty(name);
  }
}

/* ---------------------------------------------------------------------------
 * Contrast validation — plain WCAG relative-luminance math over the authored
 * hex values, so the hero registry test can fail a palette that would render
 * illegible buttons/pills/sky copy (the rules in the header comment).
 * ------------------------------------------------------------------------- */

/** WCAG relative luminance of a `#rgb`/`#rrggbb` hex colour. */
function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? [...h].map((c) => c + c).join('') : h;
  const channel = (i: number): number => {
    const v = parseInt(full.slice(i, i + 2), 16) / 255;
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

/** WCAG contrast ratio (1–21) between two hex colours. */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** The fixed light kid page the hero's `deep` text shade must read on (kid-theme.css `--page`). */
const KID_PAGE = '#faf7f5';

/**
 * Check a hero palette against the contract's contrast rules. Returns one human-readable line per violation
 * (empty array = a legible palette). Hex-only by design: the authored themes are all plain hex, and keeping
 * the checker dependency-free lets the web registry test run it over every registered hero.
 */
export function validateHeroThemeContrast(tokens: HeroThemeTokens): string[] {
  const issues: string[] = [];
  const need = (
    fg: HeroThemeVar | string,
    bg: HeroThemeVar | string,
    min: number,
    label: string,
  ) => {
    const f = fg.startsWith('--') ? tokens[fg as HeroThemeVar] : fg;
    const b = bg.startsWith('--') ? tokens[bg as HeroThemeVar] : bg;
    const r = contrastRatio(f, b);
    // A non-hex value (oklch/rgb) makes luminance NaN → contrastRatio NaN → `NaN < min` is false, silently
    // passing an unchecked palette. Hero themes are hex-only by design, so flag the unparseable input loudly.
    if (!Number.isFinite(r)) {
      issues.push(`${label}: ${f} on ${b} is not a parseable hex colour (hero themes must be hex)`);
    } else if (r < min) {
      issues.push(`${label}: ${f} on ${b} is ${r.toFixed(2)}:1, needs ≥ ${min}:1`);
    }
  };
  need('--color-primary-fg', '--color-primary', 4.5, 'button/badge text on primary');
  need('--color-primary-fg', '--color-primary-soft', 3.0, 'button text on soft (gradient top)');
  need('--color-primary', '#ffffff', 3.0, 'primary as a non-text ring/fill on a white card');
  need('--color-primary-deep', KID_PAGE, 4.5, 'deep as text on the kid page');
  need('--color-primary-deep', '--color-primary-tint', 4.5, 'deep as text on tint');
  need('--color-secondary-fg', '--color-secondary', 4.5, 'pill text on secondary');
  need('--color-accent-fg', '--color-accent', 4.5, 'accent pair');
  need('--sky-fg', '--sky-top', 4.5, 'on-sky copy over sky-top');
  need('--sky-fg', '--sky-mid', 4.5, 'on-sky copy over sky-mid');
  return issues;
}
